import { Component, inject, OnInit, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { PedidoService } from '../../core/services/pedido.service';
import { EmpresaService } from '../../core/services/empresa.service';
import { PedidoDetalleApi } from '../../core/models/api/pedido-api';
import { WhatsappUtility } from '../../core/utility/whatsapp-utility';

@Component({
    selector: 'app-seguimiento',
    standalone: true,
    imports: [CommonModule, FormsModule],
    template: `
    <main class="max-w-xl mx-auto px-4 py-10 sm:py-14 flex flex-col gap-8">
      <div class="text-center flex flex-col gap-2">
        <h1 class="text-3xl sm:text-4xl font-black text-gray-900">Rastrea tu Pedido</h1>
        <p class="text-sm text-gray-500">Ingresa tu número de pedido y correo electrónico para ver el estado de tu compra</p>
      </div>

      <div class="bg-white rounded-3xl shadow-xl shadow-gray-100 p-6 sm:p-8 flex flex-col gap-5">
        <label class="block">
          <span class="text-xs font-bold text-gray-800 block mb-2">N° de Pedido</span>
          <input [(ngModel)]="numero" placeholder="Ej: #12345" class="w-full bg-gray-50 border border-gray-200 rounded-2xl px-4 py-3 text-sm focus:outline-none focus:border-rose-400 placeholder-gray-400" />
        </label>
        <label class="block">
          <span class="text-xs font-bold text-gray-800 block mb-2">Correo Electrónico</span>
          <input [(ngModel)]="correo" placeholder="correo@ejemplo.com" class="w-full bg-gray-50 border border-gray-200 rounded-2xl px-4 py-3 text-sm focus:outline-none focus:border-rose-400 placeholder-gray-400" />
        </label>
        <p *ngIf="error()" class="text-xs font-semibold text-red-600 bg-red-50 border border-red-200 rounded-2xl p-3">{{ error() }}</p>
        <button type="button" (click)="buscar()" [disabled]="loading() || !numero().trim() || !correo().trim()" class="w-full py-4 bg-rose-500 hover:bg-rose-600 text-white font-black rounded-2xl transition-all shadow-lg shadow-rose-200 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer text-sm tracking-wide flex items-center justify-center gap-2">
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" /></svg>
          {{ loading() ? 'BUSCANDO…' : 'RASTREAR PEDIDO' }}
        </button>
      </div>

      <div *ngIf="pedido() as p" class="bg-white rounded-3xl shadow-xl shadow-gray-100 p-6 sm:p-8 flex flex-col gap-4">
        <div class="flex flex-wrap items-center justify-between gap-2">
          <h2 class="text-lg font-black text-gray-900">Pedido #{{ p.numeroPedido }}</h2>
          <span class="text-xs font-bold uppercase px-3 py-1.5 rounded-full bg-rose-50 text-rose-500">{{ p.estado }}</span>
        </div>
        <p class="text-sm text-gray-500">{{ p.fechaEntrega }} · {{ p.rangoHorario }} · Total S/ {{ p.total.toFixed(2) }}</p>
        <ul class="flex flex-col gap-1 text-sm text-gray-700">
          <li *ngFor="let it of (p.items ?? [])">{{ it.nombre }} × {{ it.cantidad }} — S/ {{ it.precioTotal.toFixed(2) }}</li>
        </ul>
        <div class="border-t border-gray-100 pt-4">
          <p class="text-xs font-bold text-gray-500 uppercase tracking-wide mb-3">Historial</p>
          <ol class="flex flex-col gap-3">
            <li *ngFor="let h of (p.historial ?? [])" class="flex gap-3 text-xs">
              <span class="text-gray-400 whitespace-nowrap">{{ h.createdAt | date:'short' }}</span>
              <span class="text-gray-700"><span class="font-bold">{{ h.estado }}</span><span *ngIf="h.comentario"> · {{ h.comentario }}</span></span>
            </li>
          </ol>
        </div>
      </div>

      <div class="bg-sky-50 rounded-3xl p-6 sm:p-8 text-center flex flex-col items-center gap-2">
        <p class="font-black text-gray-900">¿Necesitas ayuda?</p>
        <p class="text-xs text-gray-500">Si tienes problemas para rastrear tu pedido o no recibiste el correo de confirmación, contáctanos.</p>
        <a *ngIf="whatsappLink()" [href]="whatsappLink()" target="_blank" rel="noopener" class="mt-2 inline-flex items-center gap-2 px-6 py-2.5 border border-rose-400 text-rose-500 text-xs font-bold rounded-2xl hover:bg-rose-50 cursor-pointer">
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 8l7.9 5.3a2 2 0 002.2 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>
          Contactar Soporte
        </a>
      </div>
    </main>
    `
})
export class SeguimientoComponent implements OnInit {
    private pedidos = inject(PedidoService);
    private empresa = inject(EmpresaService);

    numero = signal('');
    correo = signal('');
    loading = signal(false);
    error = signal<string | null>(null);
    pedido = signal<PedidoDetalleApi | null>(null);
    whatsapp = signal('');

    whatsappLink(): string {
        return WhatsappUtility.waLink(this.whatsapp(), 'Hola, necesito ayuda con el rastreo de mi pedido.');
    }

    ngOnInit(): void {
        this.empresa.getEmpresa().subscribe({
            next: (e) => this.whatsapp.set((e?.whatsapp ?? '').replace(/\D/g, '')),
            error: () => undefined
        });
    }

    buscar() {
        if (!this.numero().trim() || !this.correo().trim()) {
            return;
        }
        this.loading.set(true);
        this.error.set(null);
        this.pedido.set(null);
        this.pedidos.rastrearPedido(this.numero().trim(), this.correo().trim()).subscribe({
            next: (d) => {
                this.pedido.set(d);
                this.loading.set(false);
            },
            error: (err) => {
                this.loading.set(false);
                this.error.set(err?.message || 'No encontramos ese pedido.');
            }
        });
    }
}
